import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import firebase from 'firebase/compat/app';
import { FormGroup, FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { ROUTE_ANIMATIONS_ELEMENTS } from '@core/core.module';
import { FileUpload } from '@core/models/FileUpload';
import { EditingStatus } from '@core/models/common-types';
import { ProductStateService } from 'app/features/admin/products/store/products.state-service';
import { removeAllSpaces } from '@shared/utils/rc-types/regex-types';
import { AuthStateService } from 'app/auth/store/auth.state-service';
import { ConfirmationService } from 'primeng/api';
import { Subject, Observable, Subscription } from 'rxjs';
import { ClientListItem } from 'app/features/admin/clients/model/Client';
import { ProductListItem, Product, dummyProductListItem } from '../../model/Product';
import { CommonModule } from '@angular/common';
import { PrimeNgModule } from '@core/primeng.module';
import { ProductType, ProductUomType } from '../../model/types';
import { getPDropdownItems } from '@core/models/common-ux-types';
import { ClientStateService } from 'app/features/admin/clients/store/client.state-service';

@Component({
  selector: 'app-edit-product',
  standalone: true,
  imports: [CommonModule, PrimeNgModule, ReactiveFormsModule],
  templateUrl: './edit-product.component.html',
  styleUrl: './edit-product.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EditProductComponent implements OnInit, OnDestroy {
  componentDestroyed$: Subject<null> = new Subject();
  selectedFile: File;
  currentFileUpload: FileUpload;
  imagePreview: string | ArrayBuffer;
  routeAnimationsElements = ROUTE_ANIMATIONS_ELEMENTS;
  editProductForm: FormGroup;
  productTypePDropdownItems = getPDropdownItems(ProductType);
  productUomTypePDropdownItems = getPDropdownItems(ProductUomType);

  selectedState: string | undefined;
  filteredOptions: Observable<string[]>;
  selectedProduct: ProductListItem;
  selectedClient: ClientListItem;
  productListItems: ProductListItem[] = [];
  productEditingStatus = EditingStatus.NOT_EDITING;
  isSuperUser = false;
  percentage = 0;
  uploadProgress$: Observable<number>;
  submitted = false;
  pageTitle = 'Edit Product Info';
  isLoading = true;
  isClientUser = false;

  minDate: Date;
  maxDate: Date;

  private subscriptions = new Subscription();
  constructor(
    private productStateService: ProductStateService,
    private authStateService: AuthStateService,
    private confirmationService: ConfirmationService,
    private clientStateService: ClientStateService,
    private formBuilder: FormBuilder,
    private changeDetector: ChangeDetectorRef,
    private router: Router,
    private route: ActivatedRoute
  ) { }

  ngOnInit() {
    this.updateTheForm(dummyProductListItem);
    this.subscriptions.add(
      this.route.params.subscribe((params) => {
        console.log('MS EditProductComponent params = ', params);
        if (params['editingStatus'] === 'EDITING') {
          this.pageTitle = 'Edit Product Info';
        } else {
          this.pageTitle = 'Enter New Product Info';
        }
      })
    );
    this.subscriptions.add(
      this.authStateService.isClientUser$.subscribe((isClientUser) => {
        this.isClientUser = isClientUser;
      })
    );
    this.subscriptions.add(
      this.clientStateService.selectedClient$.subscribe((selectedClient) => {
        // console.log('MS ProductTableComponent selectedClient = ', selectedClient);
        this.selectedClient = selectedClient;
      })
    );

    this.subscriptions.add(
      this.authStateService.isSuperUser$.subscribe((isSuperUser) => {
        this.isSuperUser = isSuperUser;
      })
    );
    this.subscriptions.add(
      this.productStateService.editingStatus$.subscribe((productEditingStatus) => {
        this.productEditingStatus = productEditingStatus;
        console.log('MS EditProductComponent productEditingStatus = ', productEditingStatus);
      })
    );
    this.subscriptions.add(
      this.productStateService.selectedProduct$.subscribe((selectedProduct) => {
        if (selectedProduct) {
          // } && this.productEditingStatus === EditingStatus.EDITING) {
          this.selectedProduct = selectedProduct;
          this.updateTheForm(this.selectedProduct);
        }
      })
    );

    this.subscriptions.add(
      this.productStateService.productListItems$.subscribe((productListItems) => {
        this.productListItems = productListItems;
      })
    );
  }

  updateTheForm(productListItem: ProductListItem) {
    console.log('MS updateTheForm productListItem= ', productListItem);

    const productTypeItem = this.productTypePDropdownItems.find((item) => item.value === productListItem.productType);

    const uomTypeItem = this.productUomTypePDropdownItems.find((item) => item.value === productListItem.uomType);

    this.editProductForm = this.formBuilder.group({
      productTypeItem,
      uomTypeItem,
      description: [productListItem.description],
      itemNumber: [productListItem.itemNumber],
      width: [productListItem.width],
      length: [productListItem.length],
      price: [productListItem.price],
      // productType: [this.selectedProduct?.productType, Validators.required],
      //  sku: [this.selectedProduct.sku],
      supplier: [productListItem.supplier],
      title: [productListItem.title],
      // uomType: [this.selectedProduct?.uomType],
      // imagePath: [this.selectedProduct?.imagePath],
      // imageUrl: [this.selectedProduct?.imageUrl],
      // imageFile: [undefined, FileValidator.maxContentSize(IMAGE_UPLOAD_MAX_SIZE)]
    });
  }
  updateDisplayName() {
    const path = removeAllSpaces(this.editProductForm.value.name, true);
    this.editProductForm.patchValue({ path });
  }

  onSaveProduct() {
    console.log('MS saveProduct this.editProductForm = ', this.editProductForm.value);
    // console.log('MS saveProduct newProduct = ', newProduct);
    const formValues = {
      description: this.editProductForm.value.description,
      itemNumber: this.editProductForm.value.itemNumber,
      length: this.editProductForm.value.length,
      price: this.editProductForm.value.price,
      productType: this.editProductForm.value.productTypeItem.value,
      supplier: this.editProductForm.value.supplier,
      title: this.editProductForm.value.title,
      //  total: this.editProductForm.value.total,
      uomType: this.editProductForm.value.uomTypeItem.value,
      updatedTimestamp: firebase.firestore.Timestamp.now(),
      width: this.editProductForm.value.width,
    };
    if (this.editProductForm.value.isEdit) {
      const newProductListItem: ProductListItem = {
        ...this.selectedProduct,
        ...formValues,
        // description: this.editProductForm.value.description,
        // itemNumber: this.editProductForm.value.itemNumber,
        // length: this.editProductForm.value.length,
        // price: this.editProductForm.value.price,
        // productType: this.editProductForm.value.productTypeItem.value,
        // supplier: this.editProductForm.value.supplier,
        // title: this.editProductForm.value.title,
        // total: this.editProductForm.value.total,
        // uomType: this.editProductForm.value.uomTypeItem.value,
        // updatedTimestamp: firebase.firestore.Timestamp.now(),
        // width: this.editProductForm.value.width,

        // imagePath: this.editProductForm.value.imagePath,
        // imageUrl: this.editProductForm.value.imageUrl,
        //  imageFile: this.editProductForm.value.imagePath ? this.editProductForm.value.imageFile[0].name : null
      };
      // console.log('MS saveProduct EDIT newProductListItem = ', newProductListItem);
      this.productStateService.updateProduct(newProductListItem);
    } else {
      const newProduct: Product = {
        clientId: this.selectedClient.id,
        ...formValues,
        // description: this.editProductForm.value.description,
        // itemNumber: this.editProductForm.value.itemNumber,
        // price: this.editProductForm.value.price,
        // productType: this.editProductForm.value.productTypeItem.value,
        // supplier: this.editProductForm.value.supplier,
        // title: this.editProductForm.value.title,
        // updatedTimestamp: firebase.firestore.Timestamp.now(),
        // uomType: this.editProductForm.value.uomTypeItem.value,
        // width: this.editProductForm.value.width,
        // length: this.editProductForm.value.length,
        // imagePath: this.editProductForm.value.imagePath,
        // imageUrl: this.editProductForm.value.imageUrl,
      };
      //  console.log('MS saveProduct ADD newProduct = ', newProduct);
      this.productStateService.addProduct(newProduct);
    }
    this.selectedProduct = undefined;
    this.productStateService.setProductEditingStatus(EditingStatus.NOT_EDITING);
    this.router.navigate(['/admin/product-list']);
  }

  // onSaveProduct() {
  //   // console.log('MS editProductForm = ', this.editProductForm.value);
  //   if (!this.editProductForm.valid) {
  //     return;
  //   }
  //   if (validatePhoneNumber(this.editProductForm.value.phone)) {
  //     this.editProductForm.patchValue({ phone: formatPhoneNumber(this.editProductForm.value.phone) });
  //   }
  //   console.log('MS onSaveProduct editProductForm = ', this.editProductForm.value);
  //   const newProduct = this.getProductFromForm();

  //   console.log('MS onSaveProduct 2 newProduct= ', newProduct);
  //   if (this.productEditingStatus !== EditingStatus.EDITING) {
  //     console.log('MS onSaveProduct this.productListItems = ', this.productListItems);
  //     const duplicate = this.productListItems.find(cl => cl.email === newProduct.email);

  //     console.log('MS onSaveProduct duplicate = ', duplicate);
  //     if (duplicate && this.productEditingStatus === EditingStatus.NOT_EDITING) {
  //       this.confirmationService.confirm({
  //         header: 'Add Product Error',
  //         message: 'The Product entered already exists!',
  //         icon: 'pi pi-exclamation-triangle',
  //         acceptLabel: 'OK',
  //         accept: () => {
  //           // this.messageService.add({ severity: 'info', summary: 'OK', detail: 'You have accepted' });
  //         },
  //       });
  //     } else {
  //       console.log('MS onSaveProduct ADDING newProduct= ', newProduct);
  //       this.productStateService.addProduct(newProduct);
  //     }
  //   } else {  // we are editing
  //     console.log('MS onSaveProduct EDITING newProduct= ', newProduct);
  //     this.productStateService.updateProduct(newProduct);
  //   }
  //   this.productStateService.setProductEditingStatus(EditingStatus.NOT_EDITING);
  //   this.router.navigate(['/admin/product-list']);
  // }

  onCancelProduct() {
    this.productStateService.setProductEditingStatus(EditingStatus.NOT_EDITING);
    this.router.navigate(['/admin/product-list']);
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }
}
